<template>
  <v-layout v-resize="onResize" column align-center justify-center>
    <v-simple-table
      fixed-header
      :height="windowHeight + 'px'"
      :style="{ width: `${windowWidth}px` }"
      dense
      class="disableOverflowAnchor"
      id="tableEditor"
      ref="tableRef"
    >
      <thead>
        <tr>
          <th key="pickNr">
            <img
              src="../../assets/weft_pick.svg"
              alt="Pick #"
              :style="{ height: headerImageHeight }"
              class="centerImg"
            />
            <div class="centerText">#</div>
          </th>
          <th v-if="showJacquardData">
            <div :style="{ width: '100%', textAlign: 'center' }">
              <img
                src="../../assets/jacquard.svg"
                alt="Jacquard"
                class="stickyImg"
                :style="{ height: headerImageHeight }"
                @click="jacquardMaximized = !jacquardMaximized"
              />
            </div>
            <div class="centerText" v-if="!jacquardMaximized">+</div>
            <div :style="{ textAlign: 'center' }" v-else>
              <template v-for="jacquardIndex in jacquardLength">
                <div
                  class="rotSquare"
                  :style="[tableStyle, squareStyle]"
                  :key="'jacquardIndex' + jacquardIndex"
                >
                  {{ jacquardIndex }}
                </div>
              </template>
            </div>
          </th>
          <th v-if="showColorControl">
            <img
              src="../../assets/color_control.svg"
              alt="Color control"
              :style="{ height: headerImageHeight }"
              class="centerImg"
              @click="colorControlMaximized = !colorControlMaximized"
            />
            <div :style="{ textAlign: 'center' }" v-if="!colorControlMaximized">
              +
            </div>
            <div :style="{ textAlign: 'center', whiteSpace: 'nowrap' }" v-else>
              <template v-for="colorControlIndex in colorControlLength">
                <div
                  v-if="colorControlIndex > 32 - colorControlAmount"
                  class="square"
                  :style="[tableStyle, squareStyle]"
                  :key="'colorControlIndex' + colorControlIndex"
                >
                  {{ 33 - colorControlIndex }}
                </div>
              </template>
              <div
                class="square"
                :style="[tableStyle, squareStyle]"
                key="colorControlRegulator"
              >
                R
              </div>
              <div
                class="square"
                :style="[tableStyle, squareStyle]"
                key="colorControlCPPP"
              >
                #
              </div>
            </div>
          </th>
          <th v-if="showShaftData">
            <img
              src="../../assets/dobby.svg"
              alt="Shaft"
              :style="{ height: headerImageHeight }"
              class="centerImg"
              @click="shaftMaximized = !shaftMaximized"
            />
            <div class="centerText" v-if="!shaftMaximized">+</div>
            <div :style="{ textAlign: 'center' }" v-else>
              <template v-for="shaftIndex in shaftLength">
                <div
                  class="square"
                  :style="[tableStyle, squareStyle]"
                  :key="'shaftIndex' + shaftIndex"
                >
                  {{ shaftIndex }}
                </div>
              </template>
            </div>
          </th>
          <template v-if="showDriveData">
            <th
              v-for="(header, index) in headers"
              :key="index"
              @click="handleColumnClick(index)"
            >
              <template v-if="header.imagePath">
                <img
                  :src="header.imagePath"
                  :alt="header.name"
                  :style="{ height: headerImageHeight }"
                  class="centerImg"
                />
              </template>
              <template v-else>
                <div>{{ header.name }}</div>
              </template>
              <div class="centerText">{{ getHeaderUnit(header) }}</div>
            </th>
          </template>
        </tr>
      </thead>
      <tbody id="tableEditorBody">
        <div :style="invisibleHeadHeight"></div>
        <tr
          class="removeRowSelection"
          v-for="(pick, pickIndex) in itemsToShow"
          :key="'pick' + (pickIndex + currentStartIndex - itemsBeforeView)"
        >
          <td
            :style="tableStyle"
            class="unmarkable"
            @click="
              () =>
                handlePickClick(pickIndex + currentStartIndex - itemsBeforeView)
            "
          >
            {{ pickIndex + 1 + currentStartIndex - itemsBeforeView }}
          </td>
          <td :style="tableStyle" class="shaftTd" v-if="showJacquardData">
            <template v-if="jacquardMaximized">
              <template
                v-if="
                  !!data.pickData.jacquardData.data[
                    pickIndex + currentStartIndex - itemsBeforeView
                  ]
                "
              >
                <JacquardRow
                  :pickIndex="pickIndex + currentStartIndex - itemsBeforeView"
                  :squareDimension="currentZoom - 1"
                  :jacquardData="
                    data.pickData.jacquardData.data[
                      pickIndex + currentStartIndex - itemsBeforeView
                    ]
                  "
                  :colorIndex="
                    getColorControlColorIndex(
                      pickIndex + currentStartIndex - itemsBeforeView
                    )
                  "
                  :ref="
                    'jacquardRow' +
                      (pickIndex + currentStartIndex - itemsBeforeView)
                  "
                ></JacquardRow>
              </template>
            </template>
          </td>
          <td :style="tableStyle" class="shaftTd" v-if="showColorControl">
            <template v-if="colorControlMaximized">
              <template
                v-if="
                  !!data.pickData.colorControl.data[
                    pickIndex + currentStartIndex - itemsBeforeView
                  ]
                "
              >
                <ColorControlRow
                  :pickIndex="pickIndex + currentStartIndex - itemsBeforeView"
                  :squareDimension="currentZoom - 1"
                  :colorControlData="
                    data.pickData.colorControl.data[
                      pickIndex + currentStartIndex - itemsBeforeView
                    ]
                  "
                  :colorControlAmount="colorControlAmount"
                  :colorIndex="
                    getColorControlColorIndex(
                      pickIndex + currentStartIndex - itemsBeforeView
                    )
                  "
                  :ref="
                    'colorControlRow' +
                      (pickIndex + currentStartIndex - itemsBeforeView)
                  "
                ></ColorControlRow>
              </template>
            </template>
          </td>
          <td :style="tableStyle" class="shaftTd" v-if="showShaftData">
            <template v-if="shaftMaximized">
              <template
                v-if="
                  !!data.pickData.shaftData.data[
                    pickIndex + currentStartIndex - itemsBeforeView
                  ]
                "
              >
                <ShaftRow
                  :pickIndex="pickIndex + currentStartIndex - itemsBeforeView"
                  :squareDimension="currentZoom - 1"
                  :shaftData="
                    data.pickData.shaftData.data[
                      pickIndex + currentStartIndex - itemsBeforeView
                    ]
                  "
                  :ref="
                    'shaftRow' +
                      (pickIndex + currentStartIndex - itemsBeforeView)
                  "
                ></ShaftRow>
              </template>
            </template>
          </td>
          <template v-if="showDriveData">
            <td
              v-for="(pickItem, pickItemIndex) in data.pickData.driveData.data[
                pickIndex + currentStartIndex - itemsBeforeView
              ]"
              :key="
                `${pickIndex +
                  currentStartIndex -
                  itemsBeforeView},${pickItemIndex}`
              "
              :style="[
                tableStyle,
                createBackground(
                  pickItemIndex,
                  pickItem,
                  pickIndex + currentStartIndex - itemsBeforeView
                )
              ]"
              @click="
                handleSelection(
                  pickIndex + currentStartIndex - itemsBeforeView,
                  pickItemIndex
                )
              "
              class="unmarkable"
              :ref="
                'driveRow' + (pickIndex + currentStartIndex - itemsBeforeView)
              "
            >
              {{ convertToFixed(pickItem, pickItemIndex) }}
            </td>
          </template>
        </tr>
        <div :style="invisibleTailHeight"></div>
      </tbody>
    </v-simple-table>
  </v-layout>
</template>
<script>
import { pickTypesSettings } from "../../utils/tableEditorHeaderHelper.js";
import { unitConverter } from "../../utils/unitConverter.js";
import JacquardRow from "./JacquardRow";
import ColorControlRow from "./ColorControlRow";
import ShaftRow from "./ShaftRow";
import eventBus from "../../utils/eventBus";
import uptFile from "../../utils/nonReactiveState/uptFile";
import selection from "../../utils/nonReactiveState/selection";
import { SET_SELECTION_PROPS } from "../../../store/modules/selection/constants.js";

const _ = require("lodash");
const debounceDelay = 50;

function mapToPercent(type, value, min, max, color) {
  const valueInPercent = ((value - min) / (max - min)) * 100;
  const mappedValue = _.clamp(valueInPercent, 0, 100);
  return {
    mappedValue,
    color
  };
}

export default {
  name: "TableEditor",

  props: {
    initialScrollPosition: { type: Number, required: false }
  },

  data: function() {
    return {
      currentStartIndex: 0,
      minItemsToShow: 20,
      maxItemsToShow: 1000,
      windowHeight: 500,
      windowWidth: 500,
      minFontSizePx: 4,
      maxFontSizePx: 20,
      jacquardMaximized: true,
      shaftMaximized: true,
      colorControlMaximized: true,
      itemsBeforeView: 0,
      currentScrollPosition: null,
      numberOfInvisibleRows: 30,
      jacquardLength: 0,
      shaftLength: 0,
      colorControlLength: 0,
      hasJacquardData: false,
      hasColorControl: false,
      hasShaftData: false
    };
  },

  components: { JacquardRow, ColorControlRow, ShaftRow },

  mounted: async function() {
    eventBus.$on("pickUpdate", args => this.updatePick(args));
    eventBus.$on("selectionUpdate", args => this.updateSelection(args));
    this.updatePickProps();
    this.$nextTick(() => {
      this.windowHeight = document.getElementById("tableEditor").offsetHeight;
      this.$refs["tableRef"].$el.firstChild.addEventListener(
        "scroll",
        _.debounce(this.onScroll, debounceDelay)
      );
      this.$refs[
        "tableRef"
      ].$el.firstChild.scrollTop = this.initialScrollPosition;
      this.onScroll();
    });
  },

  beforeDestroy: function() {
    eventBus.$off("pickUpdate", args => this.updatePick(args));
    eventBus.$off("selectionUpdate", args => this.updateSelection(args));
    if (this.$refs["tableRef"]) {
      this.$refs["tableRef"].$el.firstChild.removeEventListener(
        "scroll",
        _.debounce(this.onScroll, debounceDelay)
      );
    }
  },

  computed: {
    itemsToShow: function() {
      let result = _.floor(this.windowHeight / this.currentZoom);
      result = result + 2 * this.numberOfInvisibleRows;
      return _.clamp(
        result,
        0,
        uptFile.xmlData.patternPicksCount -
          this.currentStartIndex +
          this.itemsBeforeView
      );
    },

    showJacquardData: function() {
      return (
        this.hasJacquardData && this.$store.state.settings.showJacquardData
      );
    },

    showColorControl: function() {
      return (
        this.hasColorControl && this.$store.state.settings.showColorControl
      );
    },

    showShaftData: function() {
      return this.hasShaftData && this.$store.state.settings.showShaftData;
    },

    showDriveData: function() {
      return this.$store.state.settings.showDriveData;
    },

    currentZoom: function() {
      return this.$store.state.tableView.zoom;
    },

    headerImageHeight: function() {
      return `${_.clamp(this.currentZoom * 1.5, 20, 40)}px`;
    },

    headers: function() {
      const rawTag = uptFile.pickData.driveData.tagNames;
      return _.map(rawTag, t => pickTypesSettings[t]);
    },

    tableStyle: function() {
      const zoom = this.currentZoom;
      const fontSize = _.floor(zoom / 1.9);
      const correctedFontSize =
        fontSize < this.minFontSizePx
          ? 0
          : _.clamp(fontSize, this.maxFontSizePx);
      return {
        height: `${zoom - 1}px`,
        fontSize: `${correctedFontSize}px`
      };
    },

    squareStyle: function() {
      return {
        height: `${this.currentZoom - 1}px`,
        width: `${this.currentZoom - 1}px`
      };
    },

    invisibleHeadHeight: function() {
      return {
        height: `${this.currentZoom *
          (this.currentStartIndex - this.itemsBeforeView)}px`
      };
    },

    invisibleTailHeight: function() {
      return {
        height: `${this.currentZoom *
          (uptFile.xmlData.patternPicksCount -
            this.currentStartIndex +
            this.itemsBeforeView -
            this.itemsToShow)}px`
      };
    },

    colorControlAmount: function() {
      return uptFile.pickData.colorControl.colorControlAmount;
    },

    canSelect() {
      return this.$store.state.uptData.canSelect;
    },

    data() {
      return uptFile;
    }
  },

  methods: {
    getItemsBeforeView(zoom, startIndex) {
      return _.clamp(this.numberOfInvisibleRows, 0, startIndex);
    },

    createBackground(headerIndex, value, pickIndex) {
      let background = "";
      const type = this.headers[headerIndex];
      const settings = type.typeSetting;
      if (settings && settings.hasMinMax === true) {
        const { mappedValue, color } = mapToPercent(
          type,
          value,
          settings.minValue,
          settings.maxValue,
          settings.color
        );
        background = `linear-gradient(to right, ${color} ${mappedValue}%, transparent 0%)`;
      }

      if (
        !_.isEmpty(selection.selectionMap) &&
        selection.selectionMap[pickIndex][headerIndex]
      ) {
        if (background !== "") {
          background += ", ";
        }
        background += "rgba(100,100,100,0.25)";
      }
      return { background };
    },

    async onScroll(forceRecalculation = false) {
      const currentScrollPosition = this.$refs["tableRef"].$el.firstChild
        .scrollTop;
      if (
        this.currentScrollPosition === currentScrollPosition &&
        !(forceRecalculation === true)
      ) {
        return;
      }
      this.currentScrollPosition = currentScrollPosition;
      this.$emit("currentScrollPosition", currentScrollPosition);
      const currentStartIndex = _.floor(
        this.$refs["tableRef"].$el.firstChild.scrollTop / this.currentZoom
      );
      if (currentStartIndex !== this.currentStartIndex || forceRecalculation) {
        const itemsBeforeView = this.getItemsBeforeView(
          this.currentZoom,
          currentStartIndex
        );
        this.currentStartIndex = currentStartIndex;
        this.itemsBeforeView = itemsBeforeView;
      }
    },

    handleSelection: function(pickIndex, elementIndex) {
      if (this.canSelect) {
        selection.changeSelection([pickIndex, elementIndex]);
      }
    },

    handleColumnClick(headerIndex) {
      if (this.canSelect) {
        selection.changeColumnSelection(headerIndex);
      }
    },

    handlePickClick(pickIndex) {
      if (this.canSelect) {
        selection.changePickSelection(pickIndex);
      }
    },

    convertToFixed(pickItem, headerIndex) {
      const digitsAfterComma = this.headers[headerIndex].digitsAfterComma;
      return pickItem.toFixed(digitsAfterComma);
    },

    getHeaderUnit(header) {
      return unitConverter.lookupUnitLabel(header.showUnit, false);
    },

    onResize() {
      this.windowHeight = document.getElementById("tableEditor").offsetHeight;
      this.windowWidth = document.getElementById("tableEditor").offsetWidth;
    },

    getColorControlColorIndex(pickIndex) {
      if (!this.hasColorControl) {
        return null;
      }
      return (
        32 -
        _.findIndex(
          uptFile.pickData.colorControl.data[pickIndex][0],
          v => v === 1
        )
      );
    },

    updatePickProps() {
      this.jacquardLength = _.get(
        uptFile,
        "pickData.jacquardData.data[0].length",
        0
      );
      this.shaftLength = _.get(uptFile, "pickData.shaftData.data[0].length", 0);
      this.colorControlLength = _.get(
        uptFile,
        "pickData.colorControl.data[0][0].length",
        0
      );

      this.hasJacquardData = this.jacquardLength !== 0;
      this.hasShaftData = this.shaftLength !== 0;
      this.hasColorControl = this.colorControlLength !== 0;
    },

    updatePick(args) {
      const indexToUpdate = args.index;
      if (this.hasColorControl) {
        const colorControlRefString = `colorControlRow${indexToUpdate}`;
        const colorControlRef = this.$refs[colorControlRefString];
        if (!!colorControlRef && colorControlRef.length !== 0) {
          colorControlRef[0].draw(
            this.getColorControlColorIndex(indexToUpdate)
          );
        }
      }
      if (this.hasJacquardData) {
        const jacquardRefString = `jacquardRow${indexToUpdate}`;
        const jacquardRef = this.$refs[jacquardRefString];
        if (!!jacquardRef && jacquardRef.length !== 0) {
          jacquardRef[0].setColor(
            this.getColorControlColorIndex(indexToUpdate)
          );
          jacquardRef[0].draw();
        }
      }
      if (this.hasShaftData) {
        const shaftRefString = `shaftRow${indexToUpdate}`;
        const shaftRef = this.$refs[shaftRefString];
        if (!!shaftRef && shaftRef.length !== 0) {
          shaftRef[0].draw();
        }
      }
      const driveRefString = `driveRow${indexToUpdate}`;
      const driveRef = this.$refs[driveRefString];
      if (!!driveRef && driveRef.length !== 0) {
        driveRef.forEach((d, i) => {
          d.innerText = this.convertToFixed(
            uptFile.pickData.driveData.data[indexToUpdate][i],
            i
          );
          const newBackground = this.createBackground(
            i,
            uptFile.pickData.driveData.data[indexToUpdate][i],
            indexToUpdate
          ).background;
          d.style.background = newBackground;
        });
      }
    },

    async updateSelection(args) {
      _.forEach(args, s => {
        const driveRefString = `driveRow${s[0]}`;
        const driveRef = this.$refs[driveRefString];
        if (!!driveRef && driveRef.length !== 0) {
          driveRef[s[1]].innerText = this.convertToFixed(
            uptFile.pickData.driveData.data[s[0]][s[1]],
            s[1]
          );
          const newBackground = this.createBackground(
            s[1],
            uptFile.pickData.driveData.data[s[0]][s[1]],
            s[0]
          ).background;
          driveRef[s[1]].style.background = newBackground;
        }
      });
      await this.$store.dispatch(SET_SELECTION_PROPS);
      eventBus.$emit("selectionUpdated");
    }
  }
};
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.disableOverflowAnchor {
  overflow-anchor: none;
}

.removeRowSelection {
  background: none !important;
}

.centerImg {
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top: 5px;
}

.centerText {
  text-align: center;
  margin-bottom: 5px;
  font-size: 120%;
}

.square,
.rotSquare {
  margin: 0px 1px 5px 1px;
  box-sizing: border-box;
  display: inline-block;
  vertical-align: middle;
}

.square,
.rotSquare {
  text-align: center;
}

.rotSquare {
  transform: rotate(60deg);
}

.stickyImg {
  position: sticky;
  left: 80px;
  right: 20px;
  display: inline-block;
  margin-top: 5px;
}

thead th:first-child,
tbody td:first-child {
  position: sticky;
  left: 0;
  top: 0;
  background-color: white;
  background-clip: padding-box;
}

thead th:first-child {
  z-index: 3;
}

tbody td:first-child {
  z-index: 0;
}
</style>
